import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import SearchIcon from "@material-ui/icons/Search";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import StarIcon from "@material-ui/icons/Star";
import PersonIcon from "@material-ui/icons/Person";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  paper: {
    "& .MuiDialog-paper": {
      width: 480,
      maxWidth: "95vw",
      height: "80vh",
      display: "flex",
      flexDirection: "column",
    },
  },
  header: {
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: 0,
    ...theme.scrollbarStyles,
  },
  addBox: {
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    gap: 8,
  },
  searchBox: {
    padding: "8px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  adminChip: {
    height: 18,
    fontSize: "0.6rem",
    fontWeight: 700,
    backgroundColor: "#fef3c7",
    color: "#d97706",
    marginLeft: 6,
  },
  ownerChip: {
    height: 18,
    fontSize: "0.6rem",
    fontWeight: 700,
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    marginLeft: 6,
  },
  count: {
    padding: "6px 16px",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const GroupParticipantsModal = ({ open, onClose, groupJid, whatsappId, groupName }) => {
  const classes = useStyles();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [adding, setAdding] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (open && groupJid) loadMetadata();
    // eslint-disable-next-line
  }, [open, groupJid]);

  const loadMetadata = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${encodeURIComponent(groupJid)}/metadata`, {
        params: { whatsappId },
        timeout: 20000,
      });
      setParticipants(data.participants || []);
    } catch {
      toast.error("Erro ao carregar participantes");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const digits = newPhone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Número inválido. Use DDD + número (ex: 11999887766)");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(`/groups/${encodeURIComponent(groupJid)}/participants`, {
        whatsappId,
        participants: [digits],
        action: "add",
      });
      if (data.metadata?.participants) setParticipants(data.metadata.participants);
      setNewPhone("");
      toast.success("Participante adicionado!");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao adicionar participante");
    } finally {
      setAdding(false);
    }
  };

  const handleAction = async (jid, action) => {
    setMenuAnchor(null);
    setActionLoading(jid);
    try {
      const { data } = await api.post(`/groups/${encodeURIComponent(groupJid)}/participants`, {
        whatsappId,
        participants: [jid],
        action,
      });
      if (data.metadata?.participants) setParticipants(data.metadata.participants);
      const labels = { remove: "Removido", promote: "Promovido a admin", demote: "Rebaixado" };
      toast.success(labels[action] || "OK");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro na ação");
    } finally {
      setActionLoading(null);
    }
  };

  const getNumber = (jid) => jid?.split("@")[0] || jid;

  const filtered = participants.filter((p) => {
    if (!search) return true;
    const jid = p.id || p.jid || "";
    const number = p.number || (!jid.includes("@lid") ? getNumber(jid) : "");
    const name = (p.name || "").toLowerCase();
    const term = search.toLowerCase();
    return (number && number.includes(search.replace(/\D/g, ""))) || name.includes(term);
  });

  return (
    <Dialog open={open} onClose={onClose} className={classes.paper} maxWidth={false}>
      <Box className={classes.header}>
        <Box>
          <Typography style={{ fontWeight: 600, fontSize: "0.95rem" }}>Participantes</Typography>
          <Typography variant="caption" color="textSecondary">{groupName}</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={loadMetadata} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </Box>

      {/* Adicionar participante */}
      <Box className={classes.addBox}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Número com DDD (ex: 11999887766)"
          value={newPhone}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setNewPhone(v); }}
          style={{ flex: 1 }}
          disabled={adding}
          inputProps={{ maxLength: 15 }}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleAdd}
          disabled={adding || newPhone.length < 10}
          startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <PersonAddIcon />}
          style={{ whiteSpace: "nowrap" }}
        >
          Adicionar
        </Button>
      </Box>

      {/* Busca */}
      <Box className={classes.searchBox}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Buscar por nome ou número..."
          fullWidth
          value={search}
          onChange={(e) => { const v = e.target.value; setSearch(v); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, color: "#999" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Typography className={classes.count}>
        {filtered.length} participante{filtered.length !== 1 ? "s" : ""}
        {search && ` (filtrado de ${participants.length})`}
      </Typography>

      <DialogContent className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : participants.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4} style={{ gap: 8, color: "#667781" }}>
            <PersonIcon style={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="body2" align="center" color="textSecondary">
              Lista de participantes indisponível.
            </Typography>
            <Typography variant="caption" align="center" color="textSecondary">
              O bot pode precisar ser administrador do grupo para visualizar os participantes.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {filtered.map((p) => {
              const jid = p.id || p.jid || "";
              const isLid = p.isLid || jid.includes("@lid");
              const number = p.number || (!isLid ? getNumber(jid) : "");
              const displayName = p.name || null;
              const isSuperAdmin = p.admin === "superadmin";
              const isAdmin = p.admin === "admin" || isSuperAdmin;
              const isActing = actionLoading === jid;
              const initials = displayName ? displayName.charAt(0).toUpperCase() : null;

              // Label principal: nome > número real > "Participante" (para LIDs sem número)
              const primaryLabel = displayName || (number ? `+${number}` : "Participante");
              // Sublabel: mostra número quando já mostramos o nome
              const secondaryLabel = (displayName && number) ? `+${number}` : null;

              return (
                <ListItem key={jid} className={classes.listItem} dense>
                  <ListItemAvatar>
                    <Avatar
                      src={p.profilePicUrl || undefined}
                      style={{ width: 38, height: 38, backgroundColor: isSuperAdmin ? "#dc2626" : isAdmin ? "#d97706" : "#6b7280" }}
                    >
                      {p.profilePicUrl ? null : initials || (isSuperAdmin ? <StarIcon fontSize="small" /> : <PersonIcon fontSize="small" />)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                          {primaryLabel}
                        </span>
                        {isSuperAdmin && <Chip label="Dono" size="small" className={classes.ownerChip} />}
                        {isAdmin && !isSuperAdmin && <Chip label="Admin" size="small" className={classes.adminChip} />}
                        {isLid && !displayName && !number && (
                          <Chip label="Anônimo" size="small" style={{ height: 18, fontSize: "0.6rem", backgroundColor: "#f3f4f6", color: "#6b7280", marginLeft: 4 }} />
                        )}
                      </Box>
                    }
                    secondary={secondaryLabel ? (
                      <span style={{ fontSize: "0.75rem", color: "#667781" }}>{secondaryLabel}</span>
                    ) : null}
                  />
                  <ListItemSecondaryAction>
                    {isActing ? (
                      <CircularProgress size={20} />
                    ) : !isSuperAdmin ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget({ jid, isAdmin }); }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : null}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      {/* Menu de ações */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {menuTarget?.isAdmin ? (
          <MenuItem onClick={() => handleAction(menuTarget.jid, "demote")}>
            Rebaixar (remover admin)
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleAction(menuTarget?.jid, "promote")}>
            Promover a admin
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleAction(menuTarget?.jid, "remove")}
          style={{ color: "#dc2626" }}
        >
          Remover do grupo
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default GroupParticipantsModal;
